import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next';
import BaseButton from '@reusables/BaseButton';
import BaseModal from '@reusables/Modals/BaseModal';
import { Product } from '@/types/general'
import BaseInput from '@reusables/BaseInput';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateProductSerialNumberMutation } from '@redux/features/products/productsApi';
import { isErrorWithMessage } from '@redux/api/query';
import { toast } from 'react-toastify';

interface UpdateSerialNumberModalProperties {
    isOpen: boolean;
    onClose: () => void;
    serialNumber?: Product.Augmentations.SerialNumbers.Root;
    product: Product.Extended;
}

const formScheme = z.object({
    serial_number: z.string().nonempty(),
});

type FormTyping = z.infer<typeof formScheme>

export default function UpdateSerialNumberModal({ isOpen, onClose, serialNumber, product }: UpdateSerialNumberModalProperties) {
    const { t } = useTranslation("", { keyPrefix: "products.viewEditProduct.tabs.numbers.serialNumbers.modals.update" });

    const [updateSerialNumber, { isLoading: isUpdateLoading }] = useUpdateProductSerialNumberMutation();

    const { control, handleSubmit, setValue, reset } = useForm<FormTyping>({
        resolver: zodResolver(formScheme),
    });

    useEffect(() => {
        reset();
        if (serialNumber) {
            setValue("serial_number", serialNumber.serial_number);
        }
    }, [serialNumber]);

    const onSubmit = handleSubmit((data) => {
        if (serialNumber) {
            updateSerialNumber({
                id: serialNumber.id,
                serial_number: data.serial_number,
            }).unwrap()
                .then(() => {
                    toast.success(t("responses.success"));
                    onClose();
                })
                .catch((e) => {
                    if (isErrorWithMessage(e)) {
                        toast.error(e.message);
                    } else {
                        toast.error(t("responses.error"));
                    }
                });
        }
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            isLoading={isUpdateLoading}
            padding="56px"
            width={612}
            position="center"
            useCloseIcon
        >
            <form className="space-y-8" onSubmit={onSubmit}>
                <div className="text-xl font-semibold text-center text-accent">{t("heading")}</div>
                <div className="w-full flex items-center space-x-2">
                    <div className="flex justify-between items-center p-[16px] min-w-[200px] h-[56px] rounded-[6px] bg-[#ECEDFE]">
                        <div className="text-sm font-thin text-[#5A5587]">{t("fields.serialNumber.label")}</div>
                    </div>
                    <Controller
                        control={control}
                        name="serial_number"
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                placeholder={t("fields.serialNumber.placeholder")}
                            />
                        )}
                    />
                </div>
                <div className="grid grid-cols-2 gap-[24px]">
                    <BaseButton
                        text={t("buttons.cancel")}
                        size="md"
                        primaryOutlined
                        onClick={onClose}
                        type="button"
                    />
                    <BaseButton
                        text={t("buttons.save")}
                        size="md"
                        type="submit"
                    />
                </div>
            </form>
        </BaseModal>
    )
}
